import React from "react";
import { Checkbox, Col, Form, message, Radio, Row } from "antd";
import intersection from 'lodash/intersection';
import difference from 'lodash/difference';
import RollCallonfig from "@/config/rollCall";
import ResponsiveTable from '@/components/responsiveTable';
import { ReButton } from '@/components/refineElement';
import { getLangList } from '@/lib/i18n';
import {
  translateDay,
  dateTimeFormat3,
  dateTimeFormat,
  convertToTimestamp10,
  getCurrentTime,
  getMoment,
  getDayVacationDateParams,
} from "@/lib/dateTime";
import { sortColumn } from "@/lib/antdElementFunction";
import { hasPermission } from "@/lib/permissionUtil";

const { getRollCallInfo, updateRollCall } = RollCallonfig;

interface props {
  selectedInterval: string;
  specialDateArray: any;
}

interface state {
  roleCallArray: any;
  allRollCallTrueIds: any;
  weekPoint: number;
}

class Interval extends React.Component<props, state> {
  state = {
    roleCallArray: [] as never as any,
    allRollCallTrueIds: [],
    weekPoint: 0,
  };
  buttonT: any = getLangList("button");
  rollCallT: any = getLangList("rollCall");
  errorT: any = getLangList("error");
  formRef: any = React.createRef();

  componentDidMount() {
    this.getRollCallInfo();
  }

  getRollCallInfo = () => {
    const { weekPoint } = this.state;
    const { selectedInterval } = this.props;
    const params = {
      groupName: selectedInterval,
      startTime: convertToTimestamp10(getCurrentTime().add(weekPoint, 'weeks').startOf('isoWeek')),
      endTime: convertToTimestamp10(getCurrentTime().add(weekPoint, 'weeks').endOf('isoWeek')),
    };

    getRollCallInfo(params).then((res: any) => {
      const roleCallObject = res?.value ?? {};
      const { allStudents, allRollCallTrueIds } = roleCallObject;
      this.formRef.current.setFieldsValue({ rollId: allRollCallTrueIds });
      this.setState({ roleCallArray: allStudents, allRollCallTrueIds });
    });
  }

  columns = () => {
    const normalColumn = [
      {
        title: this.rollCallT.CLASS,
        dataIndex: "className",
        sorter: (a: any, b: any) => sortColumn(a.className, b.className),
        sortDirections: ['ascend', 'descend', 'ascend'],
        defaultSortOrder: 'ascend',
      },
      { title: this.rollCallT.CLASSROOM, dataIndex: "classroom" },
      { title: this.rollCallT.NAME, dataIndex: "nameCn", render: (text: string, record: any) => `${text}-${record.nameEn}` },
    ];
    const dateCloumn: any = this.getDateArray();

    return [...normalColumn, ...dateCloumn];
  };

  getIsLeave = (data: any, weekDay: any) => {
    const { specialDateArray } = this.props;
    const { isDayVacationDay } = getDayVacationDateParams({ specialDateArray, startDate: weekDay });
    const { rollCallMapping } = data;
    const start = weekDay.startOf('day').format(dateTimeFormat);
    const end = weekDay.endOf('day').format(dateTimeFormat);
    const todayData = rollCallMapping.find((item: any) => getMoment(item.rollCallDate).add(1, 's').isBetween(start, end));
    const { isLeave = 0 } = todayData ?? {};

    return isLeave || isDayVacationDay ? 'mark' : '';
  }

  renderOption = (rollCallMapping: any, weekDay: any) => {
    const { specialDateArray } = this.props;
    const { isDayVacationDay } = getDayVacationDateParams({ specialDateArray, startDate: weekDay });
    const start = weekDay.startOf('day').format(dateTimeFormat);
    const end = weekDay.endOf('day').format(dateTimeFormat);
    const todayData = rollCallMapping.find((item: any) => getMoment(item.rollCallDate).add(1, 's').isBetween(start, end));

    if (todayData) {
      const { rollCallId, isRollCall, isLeave = 0 } = todayData;

      return (
        <Checkbox
          key={rollCallId}
          defaultChecked={isRollCall}
          onChange={this.clickCheckbox(rollCallId)}
          disabled={isLeave || isDayVacationDay}
        />
      );
    }
    return '-';
  };

  clickCheckbox = (rollCallId: string) => (e: any) => {
    const { checked } = e.target;
    const tempRollCallIdArray = [...this.formRef.current.getFieldValue('rollId')];
    const rollCallIdArray = checked
      ? [rollCallId, ...tempRollCallIdArray]
      : tempRollCallIdArray.filter((item: any) => item !== rollCallId);

    this.formRef.current.setFieldsValue({ rollId: rollCallIdArray });
  }

  updateRollCall = () => {
    const { allRollCallTrueIds } = this.state;
    const rollId = this.formRef.current.getFieldValue('rollId');
    const intersectionKey = intersection(rollId, allRollCallTrueIds);
    const rollCallIds = difference(rollId, intersectionKey);
    const unRollCallIds = difference(allRollCallTrueIds, rollId);
    const params = { rollCallIds, unRollCallIds };

    updateRollCall(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.getRollCallInfo();
    });
  }

  getDateArray = () => {
    const { weekPoint } = this.state;
    const dateColumnArray = [];

    for (let a = 0; a <= 4; a += 1) {
      const weekDay = getCurrentTime().add(weekPoint, 'weeks').startOf('isoWeek').add(a, 'days');
      const dateAndWeek = `${weekDay.format(dateTimeFormat3)} (${translateDay(a)})`;
      dateColumnArray.push({
        title: dateAndWeek,
        dataIndex: "rollCallMapping",
        render: (data: any) => this.renderOption(data, weekDay),
        onCell: (data: any) => ({ className: this.getIsLeave(data, weekDay) }),
      });
    }
    return dateColumnArray;
  }

  changeWeekPoint = (e: any) => {
    const { value } = e.target;
    this.setState({ weekPoint: value }, this.getRollCallInfo);
  }

  render() {
    const { roleCallArray } = this.state;

    return (
      <Row gutter={[0, 12]}>
        <Col span={24}>
          <Form
            layout={"inline"}
            className="admin-form"
            ref={this.formRef}
            onFinish={this.updateRollCall}
            initialValues={{ rollId: [], weekPoint: 0 }}
          >
            <Form.Item label={this.rollCallT.TIME} name="weekPoint">
              <Radio.Group onChange={this.changeWeekPoint}>
                <Radio value={-1}>{this.rollCallT.LAST_WEEK}</Radio>
                <Radio value={0}>{this.rollCallT.THIS_WEEK}</Radio>
                <Radio value={1}>{this.rollCallT.NEXT_WEEK}</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="rollId" style={{ display: 'none' }}>
              <Checkbox.Group />
            </Form.Item>

            <div className="re-button-container-right">
              {hasPermission('ROLL_CALL_EDIT') &&
                <Form.Item>
                  <ReButton attributeObject={{ htmlType: 'submit' }}>
                    {this.buttonT.submit}
                  </ReButton>
                </Form.Item>
              }
            </div>
          </Form >
        </Col>
        <Col span={24}>
          <ResponsiveTable
            dataSource={roleCallArray}
            columns={this.columns()}
          />
        </Col>
      </Row>
    );
  }
}

export default Interval;